'use client';

import { c as _c } from "react/compiler-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipTrigger, useSidebar } from '@repo/ui/components';
import { ArchiveRestore, ArrowRightLeft, Building, CalendarCheck, Caravan, Factory, FileBarChart2, Files, KeyRound, LinkIcon, Plus, Shield, Truck, Users } from '@repo/ui/icons';
import { permissions, roleNames } from '@repo/permissions';
import { useApi } from '@repo/api';
import { useAccess } from '@repo/auth';
import { useDotEnv } from '@repo/dot-env/client';
import { useOrganisationScope } from '@repo/features/organisations';
import { getScopeOrganisationId } from '@repo/upfetch/core';
import { useQuery } from '@repo/tanstack/query';
import { useState } from 'react';
import { useCreateCompanySheet } from '@/app/@protected/_components/create-company/context.tsx';
import { CreatedCustomerSetupLinkDialog, useCustomerSetupLinkActions } from '@/app/@protected/_components/customer-setup-links';
import { NavContent } from '@/app/@protected/_components/sidebar/sidebar-content.tsx';
import { isUntargetedCustomerSetupLinkEligible } from '@/app/@protected/_utils/customer-setup-links';
export const boardsItems: NavContent = {
  items: [{
    icon: CalendarCheck,
    title: 'Operations',
    url: ''
  }, {
    icon: Truck,
    title: 'Customer Service',
    url: ''
  }, {
    icon: Files,
    title: 'Accounting',
    url: ''
  }, {
    icon: Shield,
    title: 'Risk',
    url: ''
  }],
  label: {
    title: 'Boards'
  }
};
export const adminItems: NavContent = {
  items: [{
    access: {
      match: 'any',
      permissions: [permissions.users.list]
    },
    icon: Users,
    title: 'Users',
    url: '/management/users'
  }, {
    access: {
      match: 'any',
      permissions: [permissions.permissionGroups.list],
      roles: [roleNames.superAdmin]
    },
    icon: KeyRound,
    title: 'Permissions',
    url: '/management/users/roles'
  }, {
    // The explicit role the backend recovery routes require; no permission grants it.
    access: {
      match: 'all',
      roles: [roleNames.superAdmin]
    },
    icon: ArchiveRestore,
    title: 'Saved View recovery',
    url: '/management/saved-views'
  }],
  label: {
    title: 'Admin'
  }
};
export const companyItems: NavContent = {
  items: [{
    access: {
      match: 'any',
      permissions: [permissions.companies.view]
    },
    icon: Factory,
    title: 'Customers',
    url: '/management/customers'
  }, {
    access: {
      match: 'any',
      permissions: [permissions.companies.view]
    },
    icon: Caravan,
    title: 'Carriers',
    url: '/management/carriers'
  }, {
    access: {
      match: 'any',
      permissions: [permissions.companies.view]
    },
    icon: ArrowRightLeft,
    title: 'Brokers',
    url: '/management/brokers'
  }, {
    access: {
      match: 'any',
      permissions: [permissions.companies.view]
    },
    icon: Building,
    title: 'Other companies',
    url: '/management/other-companies'
  }],
  label: {
    options: <CompanyActionsMenu />,
    title: 'Companies'
  }
};
export const otherItems: NavContent = {
  items: [{
    access: {
      match: 'any',
      permissions: [permissions.reports.commissionView, permissions.reports.loadsDataView]
    },
    icon: FileBarChart2,
    title: 'Reports',
    url: '/reports'
  }],
  label: {
    title: 'Other'
  }
};
export function CompanyActionsMenu() {
  const $ = _c(51);
  const api = useApi();
  const env = useDotEnv();
  const {
    scope
  } = useOrganisationScope();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      match: "any",
      permissions: [permissions.companies.create]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    allowed: canCreateCompany
  } = useAccess(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      match: "any",
      permissions: [permissions.customerSetupForms.tokens.create]
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    allowed: canCreateSetupLink
  } = useAccess(t1);
  const {
    openCreateCompanySheet
  } = useCreateCompanySheet();
  const {
    isMobile,
    setOpenMobile
  } = useSidebar();
  let t2;
  if ($[2] !== api.organisations.list || $[3] !== scope.kind) {
    t2 = api.organisations.list.queryOptions({
      enabled: scope.kind === "organisation"
    });
    $[2] = api.organisations.list;
    $[3] = scope.kind;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: organisations,
    isError: isOrganisationLookupError
  } = useQuery(t2);
  let t3;
  if ($[5] !== scope) {
    t3 = getScopeOrganisationId(scope);
    $[5] = scope;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const scopeOrganisationId = t3;
  let hasOrganisationLookupError;
  let t4;
  if ($[7] !== canCreateSetupLink || $[8] !== env.CUSTOMER_SETUP_FORM_URL || $[9] !== isOrganisationLookupError || $[10] !== organisations || $[11] !== scope.kind || $[12] !== scopeOrganisationId) {
    const currentOrganisationSlug = scopeOrganisationId === undefined ? undefined : organisations?.find(org => org.id === scopeOrganisationId)?.slug ?? null;
    hasOrganisationLookupError = scope.kind === "organisation" && isOrganisationLookupError;
    t4 = isUntargetedCustomerSetupLinkEligible({
      canCreate: canCreateSetupLink && !hasOrganisationLookupError,
      customerSetupFormUrl: env.CUSTOMER_SETUP_FORM_URL,
      organisationSlug: currentOrganisationSlug
    });
    $[7] = canCreateSetupLink;
    $[8] = env.CUSTOMER_SETUP_FORM_URL;
    $[9] = isOrganisationLookupError;
    $[10] = organisations;
    $[11] = scope.kind;
    $[12] = scopeOrganisationId;
    $[13] = hasOrganisationLookupError;
    $[14] = t4;
  } else {
    hasOrganisationLookupError = $[13];
    t4 = $[14];
  }
  const canCreateUntargetedSetupLink = t4;
  const isSetupLinkUnavailable = canCreateSetupLink && Boolean(env.CUSTOMER_SETUP_FORM_URL) && hasOrganisationLookupError;
  const {
    createLink,
    isPending
  } = useCustomerSetupLinkActions();
  const [isCompanyActionsOpen, setIsCompanyActionsOpen] = useState(false);
  const [createdSetupLinkUrl, setCreatedSetupLinkUrl] = useState();
  const [setupLinkError, setSetupLinkError] = useState();
  if (!canCreateCompany && !canCreateUntargetedSetupLink && !isSetupLinkUnavailable) {
    return null;
  }
  let t5;
  if ($[15] !== isMobile || $[16] !== setOpenMobile) {
    t5 = () => {
      if (isMobile) {
        setOpenMobile(false);
      }
    };
    $[15] = isMobile;
    $[16] = setOpenMobile;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const closeMobileSidebar = t5;
  let t6;
  if ($[18] !== createLink) {
    t6 = async () => {
      setSetupLinkError(undefined);
      const result = await createLink();
      if (result.status === "error") {
        setSetupLinkError(result.message);
        return;
      }
      if (result.status === "created") {
        setCreatedSetupLinkUrl(result.url);
        setIsCompanyActionsOpen(false);
      }
    };
    $[18] = createLink;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleCreateSetupLink = t6;
  let t7;
  if ($[20] !== closeMobileSidebar) {
    t7 = () => {
      setCreatedSetupLinkUrl(undefined);
      closeMobileSidebar();
    };
    $[20] = closeMobileSidebar;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleCreatedSetupLinkClose = t7;
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DropdownMenuTrigger aria-label="Company actions" className="cursor-pointer" />;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="sidebar-nav-content.tsx"><TooltipTrigger render={t8} data-sentry-element="TooltipTrigger" data-sentry-source-file="sidebar-nav-content.tsx"><Plus size={16} data-sentry-element="Plus" data-sentry-source-file="sidebar-nav-content.tsx" /></TooltipTrigger><TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="sidebar-nav-content.tsx">Company actions</TooltipContent></Tooltip>;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== canCreateCompany || $[25] !== closeMobileSidebar || $[26] !== openCreateCompanySheet) {
    t10 = canCreateCompany ? <DropdownMenuItem onClick={() => {
      closeMobileSidebar();
      openCreateCompanySheet();
    }}><Building size={16} />Create company</DropdownMenuItem> : null;
    $[24] = canCreateCompany;
    $[25] = closeMobileSidebar;
    $[26] = openCreateCompanySheet;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== canCreateUntargetedSetupLink || $[29] !== handleCreateSetupLink || $[30] !== isPending) {
    t11 = canCreateUntargetedSetupLink ? <DropdownMenuItem closeOnClick={false} disabled={isPending} onClick={() => void handleCreateSetupLink()}><LinkIcon size={16} />{isPending ? "Creating setup link\u2026" : "Create customer setup link"}</DropdownMenuItem> : null;
    $[28] = canCreateUntargetedSetupLink;
    $[29] = handleCreateSetupLink;
    $[30] = isPending;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== handleCreateSetupLink || $[33] !== setupLinkError) {
    t12 = setupLinkError ? <><p className="px-1.5 py-1 text-sm text-destructive" role="alert">{setupLinkError}</p><DropdownMenuItem closeOnClick={false} onClick={() => void handleCreateSetupLink()}>Try again</DropdownMenuItem></> : null;
    $[32] = handleCreateSetupLink;
    $[33] = setupLinkError;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== isSetupLinkUnavailable) {
    t13 = isSetupLinkUnavailable ? <DropdownMenuItem disabled={true}><LinkIcon size={16} />Customer setup link unavailable</DropdownMenuItem> : null;
    $[35] = isSetupLinkUnavailable;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13) {
    t14 = <DropdownMenuContent align="start" data-sentry-element="DropdownMenuContent" data-sentry-source-file="sidebar-nav-content.tsx">{t10}{t11}{t12}{t13}</DropdownMenuContent>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== isCompanyActionsOpen || $[43] !== t14) {
    t15 = <DropdownMenu onOpenChange={setIsCompanyActionsOpen} open={isCompanyActionsOpen} data-sentry-element="DropdownMenu" data-sentry-source-file="sidebar-nav-content.tsx">{t9}{t14}</DropdownMenu>;
    $[42] = isCompanyActionsOpen;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== createdSetupLinkUrl || $[46] !== handleCreatedSetupLinkClose) {
    t16 = <CreatedCustomerSetupLinkDialog onClose={handleCreatedSetupLinkClose} url={createdSetupLinkUrl} data-sentry-element="CreatedCustomerSetupLinkDialog" data-sentry-source-file="sidebar-nav-content.tsx" />;
    $[45] = createdSetupLinkUrl;
    $[46] = handleCreatedSetupLinkClose;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== t15 || $[49] !== t16) {
    t17 = <>{t15}{t16}</>;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
}